// Pure No-Limit Hold'em engine (Wizard Poker variant: antes only, one fixed
// first seat per hand). Rules and contract: see types.ts.
// Every export validates its inputs, clones the state (never mutates it) and
// returns the new state plus the events that happened, in order.
//
// Event notes for the server:
// - "bet" amounts are the coins moved from stack to pot by that action
//   (0 for a check). Sum of ante + bet amounts per seat == that seat's totalBet.
//   Its `action` says what happened: an "allin" request while raising is closed
//   (and the stack covers more than the call) is reported as a "call".
// - "street" carries only the newly dealt cards (3 on the flop, 1 after).
// - Payouts are never added to stacks; apply result.payouts yourself.
// - One call can run several steps: an action that closes the last betting
//   round of a hand with all-ins emits every remaining "street" then "showdown".

import { err, ok, type Result } from "neverthrow";
import { z } from "zod";
import { fullDeck } from "../cards";
import { MAX_SEATS, MIN_PLAYERS_TO_START } from "../constants";
import { shuffle, type Rng } from "../rng";
import { foldResult, houseResult, showdownResult } from "./showdown";
import type {
  EngineError,
  EngineErrorCode,
  EngineEvent,
  EnginePlayerInput,
  FoldReason,
  HandPlayer,
  HandResult,
  HandState,
  LegalActions,
  PlayerAction,
  StepOk,
  Street,
} from "./types";

type R<T> = Result<T, EngineError>;
type BettingStreet = "preflop" | "flop" | "turn" | "river";

const fail = <T>(code: EngineErrorCode, message: string): R<T> => err({ code, message });

// ---------- Input schemas (the engine never trusts its caller's data) ----------

const PlayerInputSchema = z.object({
  userId: z.string().min(1),
  username: z.string(),
  seat: z.number().int().min(0).max(MAX_SEATS - 1),
  stack: z.number().int().positive(),
});

const StartOptsSchema = z.object({
  handNumber: z.number().int().nonnegative(),
  ante: z.number().int().positive(),
  firstSeat: z.number().int().optional(),
});

const PlayerActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("fold") }),
  z.object({ type: z.literal("check") }),
  z.object({ type: z.literal("call") }),
  z.object({ type: z.literal("raise"), to: z.number().int() }),
  z.object({ type: z.literal("allin") }),
]);

const FoldReasonSchema = z.enum(["action", "timeout", "report", "knockout", "left", "kicked"]);

// ---------- Public API ----------

/**
 * Shuffle, deal 2 hole cards each (one at a time, clockwise from firstSeat),
 * collect antes. Normally events = ante x N. If at most one player still has
 * chips after the antes, the board is run out at once and the state is at showdown.
 */
export function startHand(
  players: EnginePlayerInput[],
  opts: { handNumber: number; ante: number; rng: Rng; firstSeat?: number },
): R<StepOk> {
  const parsed = z.array(PlayerInputSchema).safeParse(players);
  if (!parsed.success) {
    const field = parsed.error.issues[0]?.path[1];
    return field === "stack"
      ? fail("bad_amount", "every stack must be a positive whole number")
      : fail("bad_seat", "invalid player entry");
  }
  const inputs = parsed.data;
  if (inputs.length < MIN_PLAYERS_TO_START) return fail("not_enough_players", `need at least ${MIN_PLAYERS_TO_START} players`);
  if (inputs.length > MAX_SEATS) return fail("bad_seat", `at most ${MAX_SEATS} players`);
  if (new Set(inputs.map((p) => p.seat)).size !== inputs.length) return fail("bad_seat", "duplicate seat");
  if (new Set(inputs.map((p) => p.userId)).size !== inputs.length) return fail("bad_seat", "duplicate player");

  const o = StartOptsSchema.safeParse({ handNumber: opts.handNumber, ante: opts.ante, firstSeat: opts.firstSeat });
  if (!o.success) {
    return o.error.issues[0]?.path[0] === "firstSeat"
      ? fail("bad_seat", "invalid first seat")
      : fail("bad_amount", "handNumber and ante must be whole numbers (ante > 0)");
  }
  if (typeof opts.rng?.int !== "function") return fail("illegal_action", "rng missing");

  const sorted = [...inputs].sort((a, b) => a.seat - b.seat);
  const firstSeat = o.data.firstSeat ?? sorted[opts.rng.int(sorted.length)]!.seat;
  if (!sorted.some((p) => p.seat === firstSeat)) return fail("bad_seat", "first seat is not dealt in");

  const hand: HandPlayer[] = sorted.map((p) => ({
    userId: p.userId,
    username: p.username,
    seat: p.seat,
    stack: p.stack,
    streetBet: 0,
    totalBet: 0,
    folded: false,
    foldReason: null,
    allIn: false,
    acted: false,
    holeCards: [],
  }));

  const deck = shuffle(fullDeck(), opts.rng);
  let next = 0;
  const dealOrder = clockwise(hand, firstSeat, true);
  for (let round = 0; round < 2; round++) for (const p of dealOrder) p.holeCards.push(deck[next++]!);

  const events: EngineEvent[] = [];
  for (const p of hand) {
    const amount = Math.min(o.data.ante, p.stack);
    p.stack -= amount;
    p.totalBet += amount;
    if (p.stack === 0) p.allIn = true;
    events.push({ type: "ante", seat: p.seat, userId: p.userId, amount });
  }

  const s: HandState = {
    handNumber: o.data.handNumber,
    ante: o.data.ante,
    deck: deck.slice(next),
    community: [],
    street: "preflop",
    players: hand,
    firstSeat,
    toAct: null,
    currentBet: 0,
    lastRaiseSize: o.data.ante,
    result: null,
  };
  if (roundComplete(s)) closeRound(s, events);
  else s.toAct = firstActor(s);
  return ok({ state: s, events });
}

/** What `seat` may do now, or null if it is not that seat's turn. */
export function legalActions(state: HandState, seat: number): LegalActions | null {
  if (!isBetting(state.street) || state.toAct !== seat) return null;
  const p = state.players.find((x) => x.seat === seat);
  if (!p || !canAct(p)) return null;
  return legalFor(state, p);
}

/** The player whose turn it is acts. */
export function applyAction(state: HandState, seat: number, action: PlayerAction): R<StepOk> {
  const a = PlayerActionSchema.safeParse(action);
  if (!a.success) {
    return a.error.issues.some((i) => i.path[0] === "to")
      ? fail("bad_amount", "raise amount must be a whole number")
      : fail("illegal_action", "unknown action");
  }
  const turn = checkTurn(state, seat);
  if (turn.isErr()) return err(turn.error);

  const s = structuredClone(state);
  const p = s.players.find((x) => x.seat === seat)!;
  const events: EngineEvent[] = [];
  const acted = act(s, p, a.data, events);
  if (acted.isErr()) return err(acted.error);
  settle(s, events, seat);
  return ok({ state: s, events });
}

/** Turn timer ran out: check if that is free, otherwise fold (reason "timeout"). */
export function timeoutAction(state: HandState): R<StepOk> {
  if (!isBetting(state.street) || state.toAct === null) return fail("hand_over", "no turn is running");
  const seat = state.toAct;
  const turn = checkTurn(state, seat);
  if (turn.isErr()) return err(turn.error);
  if (turn.value.streetBet >= state.currentBet) return applyAction(state, seat, { type: "check" });

  const s = structuredClone(state);
  const events: EngineEvent[] = [];
  foldPlayer(s.players.find((x) => x.seat === seat)!, "timeout", events);
  settle(s, events, seat);
  return ok({ state: s, events });
}

/**
 * Fold a player out of turn (report, knockout, left, ...). Allowed any time
 * before "complete". Folding an already-folded player is a no-op (no events).
 * During "showdown" the result is recomputed without that player.
 */
export function forceFold(state: HandState, seat: number, reason: FoldReason): R<StepOk> {
  const why = FoldReasonSchema.safeParse(reason);
  if (!why.success) return fail("illegal_action", "unknown fold reason");
  if (state.street === "complete") return fail("hand_over", "hand is complete");
  if (!state.players.some((x) => x.seat === seat)) return fail("bad_seat", `seat ${seat} is not in this hand`);

  const s = structuredClone(state);
  const p = s.players.find((x) => x.seat === seat)!;
  const events: EngineEvent[] = [];
  if (p.folded) return ok({ state: s, events });

  foldPlayer(p, why.data, events);
  if (s.street === "showdown") {
    // Recompute who wins what; pots nobody can win go to the house.
    const live = s.players.filter((x) => !x.folded);
    const result =
      live.length === 0
        ? houseResult(s)
        : s.result?.reason === "showdown" && s.community.length === 5
          ? showdownResult(s)
          : foldResult(s, live[0]!);
    s.result = result;
    events.push({ type: "showdown", result: structuredClone(result) });
  } else {
    settle(s, events, seat);
  }
  return ok({ state: s, events });
}

/** "showdown" -> "complete". Nothing changes after this. */
/**
 * Take a player out of the hand and give back everything they put in this
 * hand (used when the host kicks someone). Their coins leave the pot, so
 * nobody can win them. Only during betting. If they had the highest bet, the
 * bet to match drops to the highest remaining one. Emits "refund" (amount may
 * be 0) and, if they were still in, "fold" with reason "kicked".
 */
export function removeWithRefund(state: HandState, seat: number): R<StepOk> {
  if (!isBetting(state.street)) return fail("hand_over", "no betting in progress");
  if (!state.players.some((x) => x.seat === seat)) return fail("bad_seat", `seat ${seat} is not in this hand`);
  const s = structuredClone(state);
  const p = s.players.find((x) => x.seat === seat)!;
  const events: EngineEvent[] = [];
  const refund = p.totalBet;
  p.stack += refund;
  p.totalBet = 0;
  p.streetBet = 0;
  p.allIn = false;
  events.push({ type: "refund", seat: p.seat, userId: p.userId, amount: refund });
  if (!p.folded) foldPlayer(p, "kicked", events);
  const highest = Math.max(0, ...s.players.filter((x) => x.seat !== seat).map((x) => x.streetBet));
  if (highest < s.currentBet) {
    s.currentBet = highest;
    s.lastRaiseSize = Math.max(s.ante, Math.min(s.lastRaiseSize, highest));
  }
  settle(s, events, seat);
  return ok({ state: s, events });
}

/**
 * Spells may change cards during the showdown window (Card Illusion,
 * Disintegrate): work the result out again from the cards as they are now.
 * Only for a real showdown (5 community cards, more than one player left).
 */
export function rescoreShowdown(state: HandState): R<StepOk> {
  if (state.street !== "showdown") return fail("hand_over", "not at showdown");
  const s = structuredClone(state);
  const events: EngineEvent[] = [];
  if (s.result?.reason === "showdown" && s.community.length === 5 && s.players.filter((p) => !p.folded).length > 1) {
    s.result = showdownResult(s);
    events.push({ type: "showdown", result: structuredClone(s.result) });
  }
  return ok({ state: s, events });
}

export function finalizeHand(state: HandState): R<HandState> {
  if (state.street === "complete") return fail("hand_over", "hand is already complete");
  if (state.street !== "showdown" || !state.result) return fail("illegal_action", "hand is still being played");
  const s = structuredClone(state);
  s.street = "complete";
  s.toAct = null;
  return ok(s);
}

// ---------- Betting ----------

function isBetting(street: Street): street is BettingStreet {
  return street === "preflop" || street === "flop" || street === "turn" || street === "river";
}

/** Still in the hand with chips behind. */
function canAct(p: HandPlayer): boolean {
  return !p.folded && !p.allIn;
}

/** Must act before the round can end. */
function needsToAct(s: HandState, p: HandPlayer): boolean {
  return canAct(p) && (!p.acted || p.streetBet < s.currentBet);
}

function checkTurn(state: HandState, seat: number): R<HandPlayer> {
  if (!isBetting(state.street)) return fail("hand_over", "no betting in progress");
  const p = state.players.find((x) => x.seat === seat);
  if (!p) return fail("bad_seat", `seat ${seat} is not in this hand`);
  if (state.toAct !== seat || !canAct(p)) return fail("not_your_turn", "not your turn");
  return ok(p);
}

/** Size of a full raise right now (never below the ante). */
function fullRaiseSize(s: HandState): number {
  return Math.max(s.lastRaiseSize, s.ante);
}

/**
 * Betting is open for `p` if they have not acted since the last full raise, or
 * if the raises since their last action add up to at least a full raise
 * (several short all-ins can do that; TDA "facing at least a full raise").
 * A player who acted and still has chips matched currentBet at that time, so
 * what they now owe (toCall) is exactly what was raised since.
 */
function bettingOpenFor(s: HandState, p: HandPlayer): boolean {
  return !p.acted || s.currentBet - p.streetBet >= fullRaiseSize(s);
}

function legalFor(s: HandState, p: HandPlayer): LegalActions {
  const toCall = Math.max(0, s.currentBet - p.streetBet);
  const maxRaiseTo = p.streetBet + p.stack;
  const fullMin = s.currentBet + fullRaiseSize(s);
  // Raising needs: betting open for this player, chips beyond a call, and
  // someone left who could still call.
  const opponentCanAct = s.players.some((o) => o.seat !== p.seat && canAct(o));
  return {
    canFold: true,
    canCheck: toCall === 0,
    callAmount: Math.min(toCall, p.stack),
    canRaise: bettingOpenFor(s, p) && maxRaiseTo > s.currentBet && opponentCanAct,
    // A stack too short for a full raise may still shove: min is capped at all-in.
    minRaiseTo: Math.min(fullMin, maxRaiseTo),
    maxRaiseTo,
  };
}

function act(s: HandState, p: HandPlayer, a: PlayerAction, events: EngineEvent[]): R<void> {
  const toCall = s.currentBet - p.streetBet;
  switch (a.type) {
    case "fold":
      foldPlayer(p, "action", events);
      return ok(undefined);
    case "check":
      if (toCall > 0) return fail("illegal_action", `cannot check: ${toCall} to call`);
      p.acted = true;
      events.push({ type: "bet", seat: p.seat, userId: p.userId, amount: 0, action: "check" });
      return ok(undefined);
    case "call":
      if (toCall <= 0) return fail("illegal_action", "nothing to call: check instead");
      put(p, Math.min(toCall, p.stack), "call", events);
      p.acted = true;
      return ok(undefined);
    case "raise":
      return raiseTo(s, p, a.to, "raise", events);
    case "allin": {
      // All-in is allowed on every turn and puts in as much as the rules let
      // this player put in: a raise when raising is open, otherwise a call.
      const to = p.streetBet + p.stack;
      if (to <= s.currentBet) {
        put(p, p.stack, "allin", events); // all-in call for the same or less
        p.acted = true;
        return ok(undefined);
      }
      if (legalFor(s, p).canRaise) return raiseTo(s, p, to, "allin", events);
      // Raising is closed (betting not reopened, or nobody left who could call
      // a raise): the stack covers more than the call, so this is just a call.
      put(p, toCall, "call", events);
      p.acted = true;
      return ok(undefined);
    }
  }
}

function raiseTo(s: HandState, p: HandPlayer, to: number, kind: "raise" | "allin", events: EngineEvent[]): R<void> {
  const legal = legalFor(s, p);
  if (!legal.canRaise) {
    return fail(
      "illegal_action",
      !bettingOpenFor(s, p) ? "betting was not reopened: call or fold" : "cannot raise now: call, check or fold",
    );
  }
  if (to < legal.minRaiseTo || to > legal.maxRaiseTo) {
    return fail("bad_amount", `raise to must be between ${legal.minRaiseTo} and ${legal.maxRaiseTo}`);
  }
  // A full raise reopens betting for everyone else; a short all-in does not on
  // its own (bettingOpenFor adds up short all-ins for players who acted).
  if (to >= s.currentBet + fullRaiseSize(s)) {
    s.lastRaiseSize = to - s.currentBet;
    for (const o of s.players) if (o.seat !== p.seat && canAct(o)) o.acted = false;
  }
  s.currentBet = to;
  put(p, to - p.streetBet, kind, events);
  p.acted = true;
  return ok(undefined);
}

/** Move coins from stack to pot. */
function put(p: HandPlayer, amount: number, action: PlayerAction["type"], events: EngineEvent[]): void {
  p.stack -= amount;
  p.streetBet += amount;
  p.totalBet += amount;
  if (p.stack === 0) p.allIn = true;
  events.push({ type: "bet", seat: p.seat, userId: p.userId, amount, action });
}

function foldPlayer(p: HandPlayer, reason: FoldReason, events: EngineEvent[]): void {
  p.folded = true;
  p.foldReason = reason;
  p.acted = true;
  events.push({ type: "fold", seat: p.seat, userId: p.userId, reason });
}

// ---------- Flow ----------

/**
 * After anything changed: end the hand if 0/1 players remain, close the round
 * if it is done, else pass the turn clockwise from `fromSeat` (an out-of-turn
 * fold keeps the current turn).
 */
function settle(s: HandState, events: EngineEvent[], fromSeat: number): void {
  const live = s.players.filter((p) => !p.folded);
  if (live.length === 0) return endHand(s, houseResult(s), events);
  if (live.length === 1) return endHand(s, foldResult(s, live[0]!), events);
  if (roundComplete(s)) return closeRound(s, events);
  const current = s.players.find((p) => p.seat === s.toAct);
  if (current && needsToAct(s, current)) return;
  s.toAct = nextToAct(s, fromSeat);
}

function roundComplete(s: HandState): boolean {
  const actors = s.players.filter(canAct);
  if (actors.every((p) => p.acted && p.streetBet === s.currentBet)) return true;
  // A lone player with chips who owes nothing has nobody left to bet against.
  return actors.length === 1 && actors[0]!.streetBet === s.currentBet;
}

/** Next street (firstSeat acts first again), or run out the board and show down. */
function closeRound(s: HandState, events: EngineEvent[]): void {
  for (const p of s.players) {
    p.streetBet = 0;
    p.acted = false;
  }
  s.currentBet = 0;
  s.lastRaiseSize = s.ante;
  s.toAct = null;
  if (s.players.filter(canAct).length <= 1) {
    while (s.street !== "river") dealNext(s, events);
    return endHand(s, showdownResult(s), events);
  }
  if (s.street === "river") return endHand(s, showdownResult(s), events);
  dealNext(s, events);
  s.toAct = firstActor(s);
}

function dealNext(s: HandState, events: EngineEvent[]): void {
  const street = s.street === "preflop" ? "flop" : s.street === "flop" ? "turn" : "river";
  const n = street === "flop" ? 3 : 1;
  const cards = s.deck.slice(0, n);
  s.deck = s.deck.slice(n);
  s.community.push(...cards);
  s.street = street;
  events.push({ type: "street", street, cards: [...cards] });
}

function endHand(s: HandState, result: HandResult, events: EngineEvent[]): void {
  s.street = "showdown";
  s.toAct = null;
  s.result = result;
  events.push({ type: "showdown", result: structuredClone(result) });
}

/** Players in clockwise order starting at `fromSeat` (inclusive) or just after it. */
function clockwise(players: readonly HandPlayer[], fromSeat: number, inclusive: boolean): HandPlayer[] {
  const ps = [...players].sort((a, b) => a.seat - b.seat);
  const i = ps.findIndex((p) => (inclusive ? p.seat >= fromSeat : p.seat > fromSeat));
  return i <= 0 ? ps : [...ps.slice(i), ...ps.slice(0, i)];
}

function firstActor(s: HandState): number | null {
  return clockwise(s.players, s.firstSeat, true).find(canAct)?.seat ?? null;
}

function nextToAct(s: HandState, fromSeat: number): number | null {
  return clockwise(s.players, fromSeat, false).find((p) => needsToAct(s, p))?.seat ?? null;
}
